import React, { useCallback, useEffect, useState } from 'react';
import {
  Card,
  Form,
  Input,
  Switch,
  Select,
  Button,
  message,
  Typography,
  Alert,
  Divider,
  Row,
  Col,
  Space,
} from 'antd';
import { SaveOutlined, ApiOutlined, CopyOutlined } from '@ant-design/icons';
import { motion } from 'framer-motion';
import { getMetaFlowHook, updateMetaFlowHook } from 'services/metaFlowHookService';
import { useMyContext } from 'Context/MyContextProvider';
import { usePermission } from '../Hooks/usePermissionGuard';
import PermissionChecker from '../Utils/PermissionChecker';
import ConditionalRender from '../Utils/ConditionalRender';

const { Text, Paragraph } = Typography;
const { Option } = Select;

const ABANDONED_LABELS = {
  5: '5 minutes',
  10: '10 minutes',
  15: '15 minutes',
  30: '30 minutes (recommended)',
  60: '1 hour',
};

const MetaFlowHook = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [minuteOptions, setMinuteOptions] = useState([5, 10, 15, 30, 60]);
  const [testEndpointUrl, setTestEndpointUrl] = useState('');

  const canEdit = usePermission('Create flows');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getMetaFlowHook();
      const data = res?.data || {};
      const options = res?.options?.abandoned_after_minutes || [5, 10, 15, 30, 60];
      setMinuteOptions(options);
      setTestEndpointUrl(res?.test_endpoint_url || '');
      form.setFieldsValue({
        enabled: data.enabled ?? false,
        url: data.url ?? '',
        send_on_completed: data.send_on_completed ?? true,
        send_on_progress: data.send_on_progress ?? false,
        send_on_abandoned: data.send_on_abandoned ?? true,
        abandoned_after_minutes: data.abandoned_after_minutes ?? 30,
      });
    } catch (error) {
      message.error('Failed to load Flow Hook settings');
    } finally {
      setLoading(false);
    }
  }, [form]);

  useEffect(() => {
    load();
  }, [load]);

  const onFinish = async (values) => {
    if (!canEdit) {
      message.warning('You do not have permission to update Flow Hook settings');
      return;
    }

    setSaving(true);
    try {
      await updateMetaFlowHook(values);
      message.success('Flow Hook settings saved');
    } catch (error) {
      message.error(
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        'Failed to save Flow Hook settings'
      );
    } finally {
      setSaving(false);
    }
  };

  const enabled = Form.useWatch('enabled', form);

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
      <Card
        title={
          <>
            <ApiOutlined className='mr-2' />
            Flow Hook
          </>
        }
        loading={loading}
      >
        <Alert
          type="info"
          showIcon
          className="mb-4"
          message="Sends flow data to your HTTPS endpoint when a Meta Flow is completed, updated, or abandoned"
          description={
            <Paragraph style={{ marginBottom: 0 }}>
              Includes merged screen responses and metadata (flow_token, wa_id, meta_flow_id). Abandoned events are sent after a delay that resets if the user continues the flow
            </Paragraph>
          }
        />
        <PermissionChecker role={'Admin'}>
          <ConditionalRender condition={testEndpointUrl}>
            <Alert
              type="warning"
              showIcon
              className="mb-4"
              message="Test endpoint (logs payload to laravel.log)"
              description={
                <Space>
                  <Text code>{testEndpointUrl}</Text>
                  <Button
                    type="link"
                    size="small"
                    icon={<CopyOutlined />}
                    onClick={async () => {
                      try {
                        await navigator.clipboard.writeText(testEndpointUrl);
                        message.success('Test URL copied');
                      } catch {
                        message.error('Copy failed');
                      }
                    }}
                  >
                    Copy
                  </Button>
                </Space>
              }
            />

          </ConditionalRender>
        </PermissionChecker>

        <Form form={form} layout="vertical" onFinish={onFinish}>
          <Row gutter={16}>
            <Col xs={24} md={6}>
              <Form.Item name="enabled" label="Enable Flow Hook" valuePropName="checked">
                <Switch disabled={!canEdit} />
              </Form.Item>
            </Col>

            <Col xs={24} md={18}>
              <Form.Item
                name="url"
                label="Endpoint URL (HTTPS)"
                rules={[
                  {
                    required: enabled,
                    message: 'HTTPS URL is required when Flow Hook is enabled',
                  },
                  { type: 'url', message: 'Enter a valid URL' },
                ]}
              >
                <Input
                  placeholder="https://api.yourserver.com/webhooks/meta-flow"
                  disabled={!canEdit}
                />
              </Form.Item>
            </Col>
          </Row>

          {enabled && (
            <>
              <Divider orientation="left">Events</Divider>

              <Row gutter={16}>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="send_on_completed"
                    label="Send when flow is completed"
                    valuePropName="checked"
                  >
                    <Switch disabled={!canEdit} />
                  </Form.Item>
                </Col>

                <Col xs={24} md={12}>
                  <Form.Item
                    name="send_on_progress"
                    label="Send on each screen hop (in progress)"
                    valuePropName="checked"
                    extra="Fires on every data_exchange with new answer data."
                  >
                    <Switch disabled={!canEdit} />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="send_on_abandoned"
                    label="Send when flow is abandoned"
                    valuePropName="checked"
                    extra="User stopped without completing; timer resets on each screen hop."
                  >
                    <Switch disabled={!canEdit} />
                  </Form.Item>
                </Col>

                <Col xs={24} md={12}>
                  <Form.Item
                    name="abandoned_after_minutes"
                    label="Mark as abandoned after"
                    rules={[{ required: true }]}
                  >
                    <Select disabled={!canEdit}>
                      {minuteOptions.map((minutes) => (
                        <Option key={minutes} value={minutes}>
                          {ABANDONED_LABELS[minutes] || `${minutes} minutes`}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
              </Row>
            </>
          )}

          {canEdit ? (
            <div className="float-right">
              <Button type="primary" htmlType="submit" icon={<SaveOutlined />} loading={saving}>
                Save settings
              </Button>
            </div>
          ) : (
            <Text type="secondary">Read-only — Create flows permission required to edit.</Text>
          )}
        </Form>
      </Card>
    </motion.div>
  );
};

export default MetaFlowHook;
